<template lang="pug">
  empty-content-list(v-if='!contents.length && !hasSearched' :type='type' :hasDrafts='hasDrafts')
  .width-100(v-else)
    search-bar(@searchedPrograms='setSearchParams' @changePageSize='changeDefaultPageSize' :drafts='type === "drafts"' isKrea)
    .empty-results.column.center.width-100.m4-top-14(v-if='!contents.length')
      img.empty-icon.m-bottom-2(src='@/assets/ic_search_empty.png' alt='No Results')
      p.error-title.title-medium.bold.dark-gray.m-bottom-2 {{ $t('searchProgramResults.errors.noResult') }}
      p.error-explanation.text-big.dark-gray {{ $t('searchProgramResults.errors.emptySearchResultExplanation') }}
    .main-container(v-show='contents.length')
      .row.space-between.m-top-3
        h1.title-big.bolder
          | {{ $t('krea.contentList.educationalContent') }}
        router-link.primary-button.with-icon.row.middle(:to='{name: routes.kreaCreateEducationalContent}')
          simple-svg.white-icon.m-right-1(:filepath='addIcon' width="20px" height="20px")
          | {{ $t('krea.contentList.createContent') }}
      paginated-table.m-bottom-5(
        :pageSize='defaultPageSize'
        :totalCount='totalCount'
        :getPage='getMoreContent'
        ref='paginatedTable'
      )
        .program-card-grid.m-top-3(v-if='navigationOptions.list')
          program-card(
            goToEdition
            v-for='content in getContentListForCard'
            :program='content'
            :key='content.id'
            withOptions
            :draft='isDrafts'
          )
        .program-card-list.m-top-3(v-if='navigationOptions.card')
          carousel-program-card(
            goToEdition
            v-for='content in contents'
            noMargin
            :program='content'
            :key='content.id'
            withOptions
            :draft='isDrafts'
          )
    krea-delete-modal(v-if='showKreaDeleteModal' @deletedLanguage='reloadContent')
</template>

<script>
import EmptyContentList from '@/components/Krea/EmptyContentList'
import SearchBar from '@/components/EducationalPrograms/SearchBar'
import ProgramCard from '@/components/EducationalPrograms/ProgramCard'
import PaginatedTable from '@/components/PaginatedTable'
import CarouselProgramCard from '@/components/EducationalProgramCard'
import addIcon from '@/assets/ic_circle-add.svg'
import { mapActions, mapGetters, mapState } from 'vuex'
import { routes } from '@/router'
import { defaultProgramsPageSize, firstPage } from '@/utils/constants'
import KreaDeleteModal from '@/components/Modals/KreaDeleteModal'

export default {
  components: { KreaDeleteModal, EmptyContentList, SearchBar, PaginatedTable, ProgramCard, CarouselProgramCard },
  props: {
    type: { type: String, required: true, default: 'published' }
  },
  data() {
    return {
      addIcon,
      pageNumber: 0,
      defaultPageSize: defaultProgramsPageSize,
      routes,
      filters: {}
    }
  },
  computed: {
    ...mapState({
      kreaContents: state => state.kreaEducationalContents,
      navigationOptions: state => state.kreaEducationalContents.navigationOptions,
      showKreaDeleteModal: state => state.behavior.kreaDeleteModal.show,
      locale: state => state.behavior.locale
    }),
    ...mapGetters([
      'getPublishedContentListForCard',
      'getDraftsContentListForCard'
    ]),
    hasSearched() {
      return Object.keys(this.filters).length
    },
    isPublished() {
      return this.type === 'published'
    },
    isDrafts() {
      return this.type === 'drafts'
    },
    contents() {
      return this.kreaContents[`${this.type}Content`].data
    },
    totalCount() {
      return this.kreaContents[`${this.type}Content`].totalCount
    },
    hasDrafts() {
      return this.kreaContents.draftsContent.totalCount
    },
    getContentListForCard() {
      return this[`get${this.type.charAt(0).toUpperCase() + this.type.slice(1)}ContentListForCard`]
    }
  },
  watch: {
    locale() {
      this.reloadContent()
    }
  },
  async created() {
    const { name } = this.$route.query
    if (name) this.filters.name = name
    await this.getContent()
    await this.hydrateCategories()
  },
  destroyed() {
    this.setKreaNavigationOptions({ option: 'list' })
  },
  methods: {
    ...mapActions([
      'hydrateCategories',
      'hydratePublishedEducationalContent',
      'hydrateDraftsEducationalContent',
      'setKreaNavigationOptions',
      'showElement',
      'hideElement'
    ]),
    setSearchParams(filters) {
      this.filters = filters
      this.$refs.paginatedTable.goToPage(firstPage, filters)
      this.$refs.searchResults.setShowResults()
    },
    clearFilters() {
      this.filters = {}
      this.defaultPageSize = defaultProgramsPageSize
    },
    async reloadContent() {
      this.pageNumber = 0
      this.filters = {}
      this.defaultPageSize = defaultProgramsPageSize
      this.getContent()
    },
    async changeDefaultPageSize(newPageSize) {
      this.defaultPageSize = newPageSize
      await this.getMoreContent(firstPage, newPageSize)
    },
    async getMoreContent(pageNumber, pageSize, filters = this.filters) {
      this.showElement({ element: 'fullPageLoader' })
      if (this.isPublished) await this.hydratePublishedEducationalContent({ pageNumber, pageSize, filters })
      if (this.isDrafts) await this.hydrateDraftsEducationalContent({ pageNumber, pageSize, filters })
      this.hideElement({ element: 'fullPageLoader' })
    },
    async getContent() {
      this.showElement({ element: 'fullPageLoader' })
      if (this.isPublished) await this.hydratePublishedEducationalContent({ filters: this.filters })
      await this.hydrateDraftsEducationalContent({ filters: this.filters })
      this.hideElement({ element: 'fullPageLoader' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/sizes';

.program-card-grid{
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.program-card-list{
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}
</style>
